import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, ButtonBase, Divider, Drawer, Grid, IconButton, TextField, Typography } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import AvatarGroup from '@material-ui/lab/AvatarGroup';
import classNames from 'classnames';

import { DateRange, DateRangeDelimiter, DateRangePicker, LocalizationProvider } from '@material-ui/pickers-next';
import { User } from '~/store/user/models';
import css from './styles.module.scss';
import { ChipsFilter } from '~/common/chips-filter';
import { Search } from '~/common/search';
import moment from 'moment';
import MomentUtils from '@material-ui/pickers-next/adapter/moment';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { getSearchTeamFilterActions, resetNewContactForm } from '../../../store/actions';
import { useSelector } from 'react-redux';
import { searchTeamFilter } from '../../../store/selectors';
import { Pagination } from '@material-ui/lab';

import { orderBy } from 'lodash';
import { useDateFormat } from '~/common/date-format';
import FilterListIcon from '@material-ui/icons/FilterList';
import { withStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { EditContactListDrawer } from '~/features/dashboard/components/new-edit-contact';
import { goToEditContactThunk } from '~/features/dashboard/store/actions.thunks';
import { useRouteMatch } from 'react-router-dom';
import { DealContactListItem, DealContactsListItem } from '~/models/contacts.dto';
import { ContactCard } from '~/features/dashboard/components/user-contacts/contact-card';
import { ContactDealCard } from '~/features/dashboard/components/user-contacts/contact-deal-card';
import { ContactCompanyCard } from '~/features/dashboard/components/user-contacts/contact-company-card';
import { v4 } from 'uuid';

interface UserContactsProps {
    className?: string;
    companies: DealContactsListItem[];
}

const ColorButton = withStyles(theme => ({
    root: {
        color: theme.palette.getContrastText('#000'),
        backgroundColor: '#000',
        '&:hover': {
            backgroundColor: '#000',
        },
        whiteSpace: 'nowrap',
        borderRadius: '20px',
        marginRight: '10px',
        minWidth: '100px',
        textTransform: 'capitalize',
    },
}))(Button);

enum DRAWER_TYPES {
    CONTACT = 'CONTACT',
    DEALCONTACT = 'DEALCONTACT',
    FILTER = 'FILTER',
}

export const MyDealContacts: React.FC<UserContactsProps> = ({ className, companies }) => {
    const match = useRouteMatch<{ creatorId: string }>();
    const { selectedDateFormat } = useDateFormat();
    const [filteredContacts, setFilteredContacts] = useState<User[] | DealContactsListItem[]>([]);
    const [rolesFilter, setRolesFilter] = useState<string[]>([]);
    const [ContactStatusFilter, setContactStatusFilter] = useState<string[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [dateFilter, setDateFilter] = React.useState<DateRange<Date>>([null, null]);

    const contacts = useMemo(() => companies.reduce((con, c) => [...con, ...c.contacts], [] as DealContactListItem[]), [companies]);
    const dispatch = useThunkDispatch();

    const roles = useMemo(() => {
        const userRoles: string[] = [];
        for (const contact of contacts) {
            if (contact.contactTypeDesc.trim() && !userRoles.includes(contact.contactTypeDesc)) {
                userRoles.push(contact.contactTypeDesc);
            }
        }
        return userRoles;
    }, [contacts]);
    const ContactStatuses = useMemo(() => {
        const contactStatuses: string[] = [];
        for (const contact of contacts) {
            if (contact.contactStatus && contact.contactStatus.trim() && !contactStatuses.includes(contact.contactStatus)) {
                contactStatuses.push(contact.contactStatus);
            }
        }
        return contactStatuses;
    }, [contacts]);

    useEffect(() => {
        const roleFilteredContacts = companies.filter(company =>
            rolesFilter.length ? !!company.contacts.find(contact => rolesFilter.includes(contact.contactTypeDesc)) : true
        );
        const filterOrder = orderBy(roleFilteredContacts, ['companyName'], ['asc']);
        const statusFilteredDeals = filterOrder.filter(company =>
            ContactStatusFilter.length ? !!company.contacts.find(contact => ContactStatusFilter.includes(contact.contactStatus)) : true
        );
        const searchFilteredDeals = statusFilteredDeals.filter(company =>
            searchValue ? !!company.contacts.find(contact => contact.kontactName.toLowerCase().includes(searchValue.toLowerCase())) : true
        );

        const [minDate, maxDate] = dateFilter;
        if (minDate && maxDate) {
            const dateFilteredDeals = searchFilteredDeals.filter(
                company =>
                    !!company.contacts.find(
                        contact =>
                            !!contact.Deals.find(deal => {
                                if (deal.startDate) {
                                    const compareDate = moment(deal.startDate);
                                    return !(compareDate.isBefore(minDate) || compareDate.isAfter(maxDate));
                                }
                                return true;
                            })
                    )
            );
            setFilteredContacts(dateFilteredDeals);
        } else {
            setFilteredContacts(searchFilteredDeals);
        }
    }, [searchValue, ContactStatusFilter, dateFilter, rolesFilter, companies]);
    const onChangeSearchFilter = (value: any) => {
        dispatch(getSearchTeamFilterActions.success({ searchTeamFilter: value }));
    };
    const getSearchDealValue = useSelector(searchTeamFilter);
    useEffect(() => {
        if (getSearchDealValue !== undefined) {
            setSearchValue(getSearchDealValue);
        }
    }, [getSearchDealValue]);

    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 9;
    const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
    const indexOfItemDeals = indexOfLastItem - ITEMS_PER_PAGE;
    const currentItems = filteredContacts.slice(indexOfItemDeals, indexOfLastItem);
    const pagesCount = Math.ceil(filteredContacts.length / ITEMS_PER_PAGE);

    const [drawerData, setDrawerData] = useState<{ isOpen: boolean; type?: DRAWER_TYPES; meta?: any }>({ isOpen: false });
    const closeDrawer = () => {
        setDrawerData({ isOpen: false, type: undefined, meta: undefined });
    };
    const openDrawer = (type: DRAWER_TYPES, meta?: any) => {
        setDrawerData({ isOpen: true, type, meta });
    };
    const openEditContactDrawer = () => {
        openDrawer(DRAWER_TYPES.CONTACT);
    };
    const openFilterDrawer = () => {
        openDrawer(DRAWER_TYPES.FILTER);
    };
    const openAddContactDrawer = () => {
        dispatch(resetNewContactForm());
        openDrawer(DRAWER_TYPES.CONTACT);
    };

    const editContact = (contactId: string) => () => {
        dispatch(goToEditContactThunk({ contactId, projectId: Number(match.params.creatorId) }));
        openEditContactDrawer();
    };

    return (
        <div className={classNames(css.module, className)}>
            <div className={css.topBar}>
                <div className={classNames(css.dateSearchContainer, css.large)}>
                    {companies.length > ITEMS_PER_PAGE && (filteredContacts.length > ITEMS_PER_PAGE || searchValue || dateFilter[0] || dateFilter[1]) && (
                        <Search search={searchValue} onSearchChange={onChangeSearchFilter} className={css.search} />
                    )}
                </div>
                <div className={css.headerSection}>
                    <div className={classNames(css.flexRows, css.contactsAvatars)}>
                        <ButtonBase>
                            <FilterListIcon className={css.filtersIcon} onClick={openFilterDrawer} />
                        </ButtonBase>
                        <ColorButton className="add-song-btn1" variant="contained" color="primary" onClick={openAddContactDrawer}>
                            Add new Contact
                        </ColorButton>
                        <AvatarGroup className={classNames(css.customAvatarGroup, 'avatars-group')} max={4}>
                            {contacts.map(({ contactImage, kontactName, kontactId }) => (
                                <Avatar className={css.customAvatar} alt={kontactName} key={kontactId || v4()} src={contactImage} />
                            ))}
                        </AvatarGroup>
                    </div>
                </div>
            </div>
            <div className={css.users}>
                <Box className={css.table}>
                    <Grid container className={css.tableHeader} spacing={2}>
                        <Grid item xs={4}>
                            <Typography variant="h6">Company</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="h6">Contacts</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="h6">Deals</Typography>
                        </Grid>
                    </Grid>

                    <Grid container spacing={3} className={css.tableBody}>
                        {(currentItems as DealContactsListItem[]).map((company: DealContactsListItem) => (
                            <Grid item xs={12} key={company.companyName.replace(' ', '_')}>
                                <Grid container className={css.tableRow} spacing={3}>
                                    <Grid item xs={4}>
                                        <ContactCompanyCard
                                            title={company.companyName}
                                            avatar={company.companyImage && `${process.env.REACT_APP_ASSET}${company.companyImage}`}
                                        />
                                    </Grid>
                                    <Grid item xs={8}>
                                        <Grid container spacing={2}>
                                            {company.contacts.map(c => (
                                                <Grid item xs={12} key={c.kontactId || v4()}>
                                                    <Grid container spacing={3}>
                                                        <Grid item xs={6}>
                                                            <Grid container>
                                                                <ContactCard contact={c} editAction={editContact(c.kontactId)} />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Grid container spacing={1}>
                                                                {c.Deals.map(
                                                                    d =>
                                                                        d.contractrefnum && (
                                                                            <Grid item xs={6} key={d.contractrefnum}>
                                                                                <Grid container>
                                                                                    <ContactDealCard deal={d} />
                                                                                </Grid>
                                                                            </Grid>
                                                                        )
                                                                )}
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
                {filteredContacts.length > ITEMS_PER_PAGE && (
                    <Box className={css.paginationWrapper}>
                        <Typography className={css.total}>Total: {filteredContacts.length}</Typography>
                        <Pagination
                            className={css.pagination}
                            count={pagesCount}
                            page={currentPage}
                            onChange={(event: any, value: any) => {
                                setCurrentPage(value);
                            }}
                            variant="outlined"
                            shape="rounded"
                            hideNextButton
                            hidePrevButton
                        />
                    </Box>
                )}
            </div>
            <Drawer variant="persistent" anchor="right" open={drawerData.isOpen} id="deal-edit-step-6-drawer" className={css.rightDrawer}>
                <Box className={css.contentWrapper}>
                    <Box className={css.closeDrawer}>
                        <IconButton onClick={closeDrawer}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography style={{ fontSize: '1rem' }}>Close</Typography>
                    </Box>
                    <Divider />
                    {drawerData.type === DRAWER_TYPES.CONTACT && <EditContactListDrawer title={'Contact'} closeDrawer={closeDrawer} />}
                    {/*{drawerData.type === DRAWER_TYPES.DEALCONTACT && <EditContactListDrawer role={11} title={'Deal Contact'} closeDrawer={closeDrawer} />}*/}
                    {drawerData.type === DRAWER_TYPES.FILTER && (
                        <div className={css.chipFilterContainer}>
                            <Typography variant="h6">Filters</Typography>
                            <LocalizationProvider dateAdapter={MomentUtils}>
                                <DateRangePicker
                                    startText="Start Date"
                                    endText="End Date"
                                    inputFormat={selectedDateFormat}
                                    value={dateFilter}
                                    onChange={newValue => setDateFilter(newValue)}
                                    renderInput={(startProps, endProps) => (
                                        <React.Fragment>
                                            <TextField {...startProps} className={css.datePickerInput} variant="standard" />
                                            <DateRangeDelimiter> to </DateRangeDelimiter>
                                            <TextField {...endProps} className={css.datePickerInput} variant="standard" />
                                        </React.Fragment>
                                    )}
                                />
                            </LocalizationProvider>
                            {roles && roles.length > 1 && (
                                <div className={css.chipFilterWrapper}>
                                    <Box pb={1}>
                                        <Typography>Contact type filters:</Typography>
                                    </Box>
                                    <ChipsFilter chips={roles} filters={rolesFilter} onFilterChange={setRolesFilter} className={css.chipFilter} />
                                </div>
                            )}
                            {ContactStatuses && ContactStatuses.length > 1 && (
                                <div className={css.chipFilterWrapper}>
                                    <Box pb={1}>
                                        <Typography>Contact status filters:</Typography>
                                    </Box>
                                    <ChipsFilter
                                        chips={ContactStatuses}
                                        filters={ContactStatusFilter}
                                        onFilterChange={setContactStatusFilter}
                                        className={css.chipFilter}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </Box>
            </Drawer>
        </div>
    );
};
